import { runtimeConfig } from '@/config/runtime-config'

const storageKey = 'user_oauth_authorization_return_url'
const maxAgeMs = 10 * 60 * 1000

interface StoredAuthorizationReturn {
  createdAt: number
  url: string
}

/**
 * 只接受当前 Hub API origin 下的 `/oidc/authorize`，防止登录页成为任意站外跳转器。
 */
export function normalizeOAuthAuthorizationReturnUrl(
  value: string | null | undefined,
  apiBaseUrl = runtimeConfig.apiBaseUrl,
  frontendOrigin = window.location.origin,
) {
  if (!value?.trim()) return undefined

  try {
    const apiUrl = new URL(apiBaseUrl, frontendOrigin)
    const candidate = new URL(value, apiUrl.origin)
    if (
      candidate.origin !== apiUrl.origin ||
      candidate.pathname !== '/oidc/authorize' ||
      candidate.username ||
      candidate.password ||
      candidate.hash
    ) {
      return undefined
    }
    return candidate.toString()
  } catch {
    return undefined
  }
}

export function persistOAuthAuthorizationReturnUrl(value: string | null | undefined) {
  const normalized = normalizeOAuthAuthorizationReturnUrl(value)
  try {
    if (!normalized) {
      window.sessionStorage.removeItem(storageKey)
      return
    }
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        createdAt: Date.now(),
        url: normalized,
      } satisfies StoredAuthorizationReturn),
    )
  } catch {
    // sessionStorage 失败时登录仍可使用，只是不恢复 OAuth authorization request。
  }
}

export function readOAuthAuthorizationReturnUrl() {
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    if (!raw) return undefined
    const stored = JSON.parse(raw) as Partial<StoredAuthorizationReturn>
    if (
      typeof stored.createdAt !== 'number' ||
      Date.now() - stored.createdAt > maxAgeMs ||
      typeof stored.url !== 'string'
    ) {
      window.sessionStorage.removeItem(storageKey)
      return undefined
    }
    const normalized = normalizeOAuthAuthorizationReturnUrl(stored.url)
    if (!normalized) window.sessionStorage.removeItem(storageKey)
    return normalized
  } catch {
    window.sessionStorage.removeItem(storageKey)
    return undefined
  }
}

export function consumeOAuthAuthorizationReturnUrl() {
  const value = readOAuthAuthorizationReturnUrl()
  window.sessionStorage.removeItem(storageKey)
  return value
}
